import { BaseSyntheticEvent } from "react";
import { AxiosError } from "axios";
import { useAuthorizationContext, useClientPortalConfigContext } from "../../../../context";
import {
    AccountModel,
    ResetPasswordFailedApiDetailsErrorResponse,
    RegisterModel,
    ResendVerificationCodeModel,
} from "types/generated/models";
import { UseFormReturn } from "react-hook-form";
import { formatPhoneNumber, RegisterType, Step } from "./RegisterDialogForm.helpers";
import { parseDigits } from "libphonenumber-js";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { Typography } from "@mui/material";
import { RegisterMode } from "../../Account.types";
import { is400Error, usePostMutation, useDefaultHandleAxiosErrorProps } from "hooks/useClientPortalQuery";
import { handleAxiosError } from "hooks/useClientPortalQuery.helpers";
import { useAnalytics } from "hooks/useAnalytics";
import { RecaptchaAction } from "utils/recaptcha";

export interface IUserRegisterHook {
    isPending: boolean;
    isResending: boolean;
    register: (values: RegisterType, event: BaseSyntheticEvent, registerCompleted?: () => void) => Promise<void>;
    resendVerificationCode: () => Promise<void>;
}

export function useClientPortalClientRegister(
    form: UseFormReturn<RegisterType>,
    registerMode: RegisterMode
): IUserRegisterHook {
    const { clientPortalGuid, clientPortalConfig } = useClientPortalConfigContext();
    const { getValues, setValue, reset, setError, clearErrors } = form;
    const { loginUser } = useAuthorizationContext();
    const { track } = useAnalytics();
    const errorProps = useDefaultHandleAxiosErrorProps();

    const suppress4xxErrors = (error: AxiosError) => {
        if (!is400Error(error)) {
            handleAxiosError({ ...errorProps, error });
        }
    };

    const isCreateMode = registerMode === RegisterMode.Create;

    const { mutateAsync, isPending } = usePostMutation<AccountModel, AxiosError, RegisterModel>({
        url: `/api/v1/${clientPortalGuid}/Auth/${isCreateMode ? "Register" : "Recover"}`,
        handleCustomError: suppress4xxErrors,
        // Recover is not scored, so no token is minted for it.
        recaptchaAction: isCreateMode ? RecaptchaAction.Register : undefined,
    });
    const { mutateAsync: resendMutateAsync, isPending: isResending } = usePostMutation<
        void,
        AxiosError,
        ResendVerificationCodeModel
    >({
        url: `/api/v1/${clientPortalGuid}/Auth/ResendVerificationCode`,
        handleCustomError: suppress4xxErrors,
        recaptchaAction: RecaptchaAction.ResendVerificationCode,
    });

    const register = async (values: RegisterType, event: BaseSyntheticEvent, registerCompleted?: () => void) => {
        if (values.phoneNumber) {
            const countryCode = clientPortalConfig.countryCode;
            const formattedMobileNumber = formatPhoneNumber(values.phoneNumber, countryCode);
            values.phoneNumber = parseDigits(formattedMobileNumber);
        }

        try {
            const response = await mutateAsync(values as RegisterModel);

            if (values.step === Step.Initial) {
                setValue("step", Step.Verification);
                event.preventDefault();
            } else if (response?.clientId) {
                loginUser(response);
                track("user_registration", { method: "email" });
                registerCompleted?.();
                enqueueSnackbar(<Typography variant="body2">Account has been successfully created.</Typography>, {
                    variant: "success",
                    alertTitle: "Account created",
                });
            }
        } catch (error) {
            const typedError = error as AxiosError<ResetPasswordFailedApiDetailsErrorResponse>;
            if (is400Error(typedError)) {
                if (typedError.response?.data?.details?.tokenError && values.step === Step.Initial) {
                    setValue("step", Step.Verification);
                    setError("root", {
                        type: "manual",
                        message: typedError.response?.data?.message || "An error occurred during registration.",
                    });
                } else if (values.step === Step.Initial) {
                    enqueueSnackbar(
                        <Typography variant="body2">
                            {typedError.response?.data?.message || "An error occurred during registration."}
                        </Typography>,
                        { variant: "error", alertTitle: "Registration failed" }
                    );
                } else {
                    setError("root", {
                        type: "manual",
                        message: typedError.response?.data?.message || "An error occurred during registration.",
                    });
                }
            }
        } finally {
            const formValues = getValues();
            reset(formValues, {
                keepErrors: true,
            });
        }
    };

    const resendVerificationCode = async () => {
        const values = getValues();
        const countryCode = clientPortalConfig.countryCode;

        const resendData: ResendVerificationCodeModel = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phoneNumber ? parseDigits(formatPhoneNumber(values.phoneNumber, countryCode)) : null,
        };

        try {
            await resendMutateAsync(resendData);
            clearErrors("root");
            enqueueSnackbar(<Typography variant="body2">Verification code has been resent.</Typography>, {
                variant: "success",
                alertTitle: "Code resent",
            });
        } catch (error) {
            const typedError = error as AxiosError<ResetPasswordFailedApiDetailsErrorResponse>;
            if (is400Error(typedError)) {
                setError("root", {
                    type: "manual",
                    message:
                        typedError.response?.data?.message ||
                        "An error occurred while resending the verification code.",
                });
            }
        }
    };

    return {
        isPending,
        isResending,
        register,
        resendVerificationCode,
    };
}
